import React, { useCallback } from 'react';

import {
  ActionIcon,
  Group,
  Paper,
  Skeleton,
  Title,
  Tooltip,
  createStyles,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilterOff } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import HelpButton from 'components/HelpButton';
import dayjs from 'dayjs';
import useAthletesAtRisk from 'hooks/reports/athletes/useAthletesAtRisk';
import { useNavigate } from 'react-router-dom';
import { AtRiskReport } from 'types/generated';
import { setDateDifference } from 'utils/date-functions';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content auto max-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title dateInput clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content auto min-content min-content',
      gridTemplateAreas: `
        'backButton title clear download'
        'dateInput dateInput dateInput dateInput'
        `,
    },
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.sm,
      gridTemplateColumns: 'min-content auto min-content',
      gridTemplateAreas: `
      'backButton title clear'
      'dateInput dateInput dateInput'
      `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  dateInput: {
    gridArea: 'dateInput',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: AtRiskReport): { [key: string]: any } => ({
  'Full Name': d.fullName,
  'Current Status': d.athleteStatus,
  Email: d.email,
  Phone: d.phone,
  'Package End Date': dayjs(d.lastPackageEndDate).format('YYYY-MM-DD'),
});

const AthletesAtRiskPageHeader = ({
  initialSearchFiltersValue,
  searchFilters,
  setSearchFilters,
  orderingValue,
}: {
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  initialSearchFiltersValue: SearchFilters;
  orderingValue: string;
}): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { classes } = useStyles();
  const navigate = useNavigate();

  const minDate = setDateDifference(new Date(), 0, 0, 1);

  const { data, isLoading, isSuccess, isError } = useAthletesAtRisk({
    date: searchFilters.date.toISOString(),
  });

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const handleDateChange = (value: DateValue): void => {
    if (value instanceof Date) {
      const adjustedDate = setDateDifference(value, 0, 0, 0, 23, 59, 59);
      setSearchFilters(prev => ({ ...prev, date: adjustedDate }));
    }
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  const getAthletesAtRiskCount = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Title order={isDesktop ? 1 : 3} color='red'>
          Error
        </Title>
      );
    }
    if (isSuccess && data.pages) {
      return (
        <Title order={isDesktop ? 1 : 3} color='gray'>
          {data.pages[0].data.count}
        </Title>
      );
    }
    return <></>;
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title} data-cy='athletesAtRiskPageHeader'>
        <Title order={isDesktop ? 1 : 3}>Athletes At Risk</Title>
        {getAthletesAtRiskCount()}
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm'>
            Athletes with a currently active package without a package on or after the selected date.
          </Text>
        </HelpButton>
      </Group>
      <DatePickerInput
        className={classes.dateInput}
        value={searchFilters.date}
        onChange={handleDateChange}
        valueFormat='MMMM DD, YYYY'
        type='default'
        allowDeselect={false}
        required
        minDate={minDate}
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={useAthletesAtRisk}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{ date: searchFilters.date.toISOString(), ordering: orderingValue }}
        className={classes.download}
        reportType='athletes-at-risk'
        startDate={dayjs(searchFilters.date).format('YYYY-MM-DD')}
        endDate={dayjs(searchFilters.date).format('YYYY-MM-DD')}
      />
    </Paper>
  );
};

export default AthletesAtRiskPageHeader;
